<template>
  <div class="p-8 bg-[#F8FAFC] min-h-screen">
    <header class="flex  md:flex-row justify-between items-start md:items-center mb-10 gap-4">
      <div>
        <h1 class="text-3xl font-extrabold text-[#222222] tracking-tight">Configuration Système & Provisioning</h1>
        <p class="text-gray-500 font-medium">Générez des accès, liez des ressources et gérez les endpoints clients.</p>
      </div>
      <div class="flex gap-3">
        <button class="bg-blue-500 text-white px-6 py-3 rounded-xl font-bold text-sm shadow-lg hover:brightness-110 transition flex items-center gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
          Générer une URL de diffusion
        </button>
      </div>
    </header>

    <div class="grid lg:grid-cols-12 gap-8">
      
      <div class="lg:col-span-8 space-y-8">
        
        <div class="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-100">
          <h2 class="text-sm font-black uppercase tracking-[0.2em] text-gray-400 mb-8">Provisioning de Nouvel Appareil</h2>
          
          <div class="grid md:grid-cols-2 gap-6">
            <div class="space-y-2">
              <label class="text-xs font-bold text-gray-700">Client Bénéficiaire</label>
              <select class="w-full bg-gray-50 border-none rounded-xl py-3 px-4 text-sm font-medium focus:ring-2 focus:ring-dimotec-orange/20">
                <option v-for="c in clients" :key="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="space-y-2">
              <label class="text-xs font-bold text-gray-700">Type d'appareil</label>
              <select class="w-full bg-gray-50 border-none rounded-xl py-3 px-4 text-sm font-medium focus:ring-2 focus:ring-dimotec-orange/20">
                <option>Borne Android (APK)</option>
                <option>Écran TV (Dynamic Render)</option>
                <option>Tablette Console Appel</option>
              </select>
            </div>
            <div class="md:col-span-2 space-y-2">
              <label class="text-xs font-bold text-gray-700">Identifiant Unique (UID / Mac Address)</label>
              <input type="text" placeholder="Ex: DF-MONTPELLIER-001" class="w-full bg-gray-50 border-none rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-dimotec-orange/20">
            </div>
          </div>

          <div class="mt-8 pt-6 border-t border-gray-50 flex justify-end">
            <button class="px-8 py-3 bg-[#222222] text-white rounded-xl font-bold text-sm hover:bg-black transition">
              Lier la ressource au client
            </button>
          </div>
        </div>

        <div class="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
          <div class="p-8 border-b border-gray-50">
            <h2 class="text-sm font-black uppercase tracking-[0.2em] text-gray-400">Ressources en Service</h2>
          </div>
          <table class="w-full">
            <thead class="bg-gray-50 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
              <tr>
                <th class="px-8 py-4">Client</th>
                <th class="px-6 py-4">Type</th>
                <th class="px-6 py-4">URL / Clé</th>
                <th class="px-8 py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-50">
              <tr v-for="item in activeLinks" :key="item.id" class="hover:bg-gray-50/50 transition-colors">
                <td class="px-8 py-5">
                  <span class="font-bold text-gray-900 text-sm">{{ item.client }}</span>
                </td>
                <td class="px-6 py-5 text-xs font-medium text-gray-500">{{ item.type }}</td>
                <td class="px-6 py-5">
                  <div class="flex items-center gap-2">
                    <code class="bg-gray-100 px-2 py-1 rounded text-[10px] text-dimotec-orange font-bold">{{ item.key }}</code>
                    <button class="text-gray-300 hover:text-gray-600">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    </button>
                  </div>
                </td>
                <td class="px-8 py-5 text-right">
                  <button class="text-red-400 hover:text-red-600 font-bold text-xs uppercase">Révoquer</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="lg:col-span-4 space-y-8">
        
        <div class="bg-[#222222] rounded-[2.5rem] p-8 text-white shadow-xl">
          <div class="flex items-center gap-4 mb-8">
            <div class="p-3 bg-blue-500 rounded-2xl">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-7-1h.01" /></svg>
            </div>
            <div>
              <h3 class="text-xs font-black uppercase tracking-widest text-dimotec-orange">Gateway WebSocket</h3>
              <p class="text-[10px] text-gray-400 italic">Instance de routage active</p>
            </div>
          </div>

          <div class="space-y-4">
             <div class="bg-white/5 p-4 rounded-xl border border-white/10">
               <p class="text-[10px] text-gray-500 uppercase font-bold mb-1">Endpoint Principal</p>
               <p class="text-xs font-mono">wss://api.datafuse.io/live/v1</p>
             </div>
             <div class="bg-white/5 p-4 rounded-xl border border-white/10">
               <p class="text-[10px] text-gray-500 uppercase font-bold mb-1">Time-to-Live (Sessions TV)</p>
               <p class="text-xs font-mono">86400s (24 Heures)</p>
             </div>
          </div>
          
          <button class="w-full mt-8 py-3 bg-white/10 rounded-xl text-xs font-bold hover:bg-white/20 transition">Tester la latence (Ping)</button>
        </div>

        <div class="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-sm">
          <h3 class="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-6">Sécurité SaaS</h3>
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <span class="text-xs font-bold text-gray-600">Double Auth (Techniciens)</span>
              <div class="w-10 h-5 bg-green-500 rounded-full flex items-center px-1">
                <div class="w-3 h-3 bg-white rounded-full ml-auto"></div>
              </div>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-xs font-bold text-gray-600">Auto-révocation 30j</span>
              <div class="w-10 h-5 bg-gray-200 rounded-full flex items-center px-1">
                <div class="w-3 h-3 bg-white rounded-full"></div>
              </div>
            </div>
          </div>
          
          <div class="mt-8 p-4 bg-blue-50 border border-blue-100 rounded-2xl">
            <p class="text-[10px] text-dimotec-orange font-bold uppercase mb-1">Alerte Infrastructure</p>
            <p class="text-[11px] text-blue-700 leading-tight">Le certificat SSL du cluster Montpellier expire dans 14 jours.</p>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      clients: [
        { id: 1, name: "Media Link Solutions" },
        { id: 2, name: "Dimotec Montpellier" },
        { id: 3, name: "Pharmacie du Soleil" }
      ],
      activeLinks: [
        { id: 101, client: "Media Link", type: "TV Dynamic Render", key: "https://tv.datafuse.io/render/ML-99" },
        { id: 102, client: "Dimotec", type: "Borne Android APK", key: "AK-882-991-X" },
        { id: 103, client: "Media Link", type: "Borne Android APK", key: "AK-112-445-Z" },
        { id: 104, client: "Pharmacie Soleil", type: "Console Tablette", key: "https://admin.datafuse.io/app/ps-22" }
      ]
    }
  }
}
</script>